// Classes
import BaseService from './base-service';

// Utils
import { getBackendURL } from 'libs/utils/url';

// Constants
import { API_BASE_PATH, API_V2_BASE_PATH } from 'libs/utils/constants';


/**
 * API full hostname URL.
 * @private
 */
const API_URL = getBackendURL();

/**
 * Package's API Documentation endpoint. Interpolation: `{{pkgName}}`, `{{version}}`
 * @private
 */
const API_DOC_PACKAGE_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/packages/{{pkgName}}/{{version}}/documentation`;

/**
 * Event's API Documentation endpoint. Interpolation: `{{eventId}}`
 * @private
 */
const API_DOC_EVENT_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/workspace/{{eventId}}/documentation`;

/**
 * Generic event's API Documentation endpoint.
 * @private
 */
const API_DOC_EVENTS_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/workspace/documentation`;

/**
 * Organization's API Documentation endpoint. Interpolation: `{{orgId}}`
 * @private
 */
const API_DOC_ORG_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/orgs/{{orgId}}/documentation`;

/**
 * Generic organization's API Documentation endpoint
 * @private
 */
const API_DOC_ORGS_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/orgs/documentation`;

/**
 * Public available packages and their versions endpoint
 * @private
 */
const PUBLIC_PACKAGES_ENDPOINT = `${API_URL}${API_BASE_PATH}/packages`;

/**
 * Direct data API documentation endpoint
 * @private
 */
const API_DOC_DIRECT_DATA_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/orgs/documentation/data`;

/**
 * Direct data files API documentation endpoint
 * @private
 */
const API_DOC_DIRECT_DATA_FILES_ENDPOINT = `${API_URL}${API_V2_BASE_PATH}/orgs/documentation/data-files`;


/**
 * Provides utils for getting API documentation data.
 *
 * @example
 * import APIDocService from 'libs/services';
 * ...
 * const apiDoc = new APIDocService();
 */
export default class APIDocService extends BaseService {
    /**
     * Given an URL this method returns all API docs details.
     *
     * @param {String} url the url from which getting the documentation from
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getDocs(url) {
        const { data } = await this.getCached(url);
        return data;
    }

    /**
     * Given a package name and a version this method returns all API docs details URL.
     *
     * @param {String} packageName the name of the package
     * @param {String} version the version of the package
     *
     * @return {String} the event's documentation URL
     */
    getPackageDocsUrl(packageName, version) {
        return API_DOC_PACKAGE_ENDPOINT
            .replace('{{pkgName}}', packageName)
            .replace('{{version}}', version);
    }

    /**
     * Given a package name and a version this method returns all API docs details.
     *
     * @param {String} packageName the name of the package
     * @param {String} version the version of the package
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getPackageDocs(packageName, version) {
        return this.getDocs(this.getPackageDocsUrl(packageName, version));
    }

    /**
     * Given an event id this method returns all API docs details URL.
     *
     * @param {String} [eventId] the ID of the event
     *
     * @return {String} the event's documentation URL
     */
    getEventDocsUrl(eventId) {
        return eventId ? API_DOC_EVENT_ENDPOINT.replace('{{eventId}}', eventId) : API_DOC_EVENTS_ENDPOINT;
    }

    /**
     * Given an event id this method returns all API docs details.
     *
     * @param {String} [eventId] the ID of the event
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getEventDocs(eventId) {
        return this.getDocs(this.getEventDocsUrl(eventId));
    }

    /**
     * Given an organization id this method returns all API docs details URL.
     *
     * @param {String} [orgId] the ID of the organization
     *
     * @return {String} the organization's documentation URL.
     */
    getOrgDocsUrl(orgId) {
        return orgId ? API_DOC_ORG_ENDPOINT.replace('{{orgId}}', orgId) : API_DOC_ORGS_ENDPOINT;
    }

    /**
     * Given an organization id this method returns all API docs details.
     *
     * @param {String} [orgId] the ID of the organization
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getOrgDocs(orgId) {
        return this.getDocs(this.getOrgDocsUrl(orgId));
    }

    /**
     * Gets the direct data API docs details URL.
     *
     * @return {String} the direct data documentation URL.
     */
    getDirectDataDocsUrl() {
        return API_DOC_DIRECT_DATA_ENDPOINT;
    }

    /**
     * Gets the direct data files API docs details URL.
     *
     * @return {String} the direct data files documentation URL.
     */
    getDirectDataFilesDocsUrl() {
        return API_DOC_DIRECT_DATA_FILES_ENDPOINT;
    }

    /**
     * Gets the direct data API docs details.
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getDirectDataDocs() {
        return this.getDocs(this.getDirectDataDocsUrl());
    }


    /**
     * Gets the direct data files API docs details.
     *
     * @return {Promise} Promise object that represents the API's documentation.
     */
    async getDirectDataFilesDocs() {
        return this.getDocs(this.getDirectDataFilesDocsUrl());
    }

    /**
     * Gets the public available packages
     *
     * @returns {Promise<import('axios').AxiosResponse>} the server response
     */
    async getPackages() {
        const { data } = await this.getCached(PUBLIC_PACKAGES_ENDPOINT);

        return data;
    }
}
